import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { CORE_SPELLS } from "../../catalogs/spellsCatalog";
import DraggableModal from "../../components/layout/DraggableModal/DraggableModal";

const SPELL_LIST_KEYS_BY_CLASS_ID = {
  bard: ["Bard"],
  cleric: ["Cleric"],
  druid: ["Druid"],
  paladin: ["Paladin"],
  ranger: ["Ranger"],
  sorcerer: ["Sorcerer/Wizard"],
  wizard: ["Sorcerer/Wizard"],
};

const formatClasses = (spell) =>
  Object.entries(spell.classLevels)
    .map(([spellClass, level]) => `${spellClass} ${level}`)
    .join(" / ");

const resolveAllowedSpellLists = (character) => {
  const spellcastingClasses = character?.ruleDerived?.spellcasting || [];
  if (spellcastingClasses.length === 0) return [];

  return [...new Set(
    spellcastingClasses.flatMap((entry) => SPELL_LIST_KEYS_BY_CLASS_ID[entry.classId] || [])
  )];
};

const SpellPickerModal = ({ isOpen, onClose, selectedSpells, onSelect, character }) => {
  const { i18n, t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const selectedCatalogIds = new Set(selectedSpells.map((spell) => spell.catalogId));
  const allowedSpellLists = useMemo(() => resolveAllowedSpellLists(character), [character]);
  const classes = useMemo(
    () => [...new Set(CORE_SPELLS
      .flatMap((spell) => Object.keys(spell.classLevels))
      .filter((spellClass) => allowedSpellLists.length === 0 || allowedSpellLists.includes(spellClass))
    )].sort(),
    [allowedSpellLists]
  );
  const spells = useMemo(
    () =>
      CORE_SPELLS.filter((spell) => {
        const matchesSearch = `${spell.name.ru} ${spell.name.en}`
          .toLowerCase()
          .includes(search.toLowerCase());
        const matchesAllowedClass =
          allowedSpellLists.length === 0
          || Object.keys(spell.classLevels).some((spellClass) => allowedSpellLists.includes(spellClass));
        const matchesClass =
          !selectedClass || Object.hasOwn(spell.classLevels, selectedClass);
        const matchesLevel =
          selectedLevel === "" ||
          Object.entries(spell.classLevels).some(([spellClass, level]) =>
            (allowedSpellLists.length === 0 || allowedSpellLists.includes(spellClass)) && level === Number(selectedLevel)
          );
        return matchesSearch && matchesAllowedClass && matchesClass && matchesLevel;
      }),
    [allowedSpellLists, search, selectedClass, selectedLevel]
  );
  const useEnglishCatalog = (i18n.resolvedLanguage || i18n.language) === "en";

  return (
    isOpen && (
      <DraggableModal
        isVisible={isOpen}
        onClose={onClose}
        headerText={t("spells.choose")}
        className="spell-picker-modal"
      >
        <label className="spell-picker-search">
          {t("spells.catalogSearch")}
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("spells.catalogPlaceholder")}
          />
        </label>
        <div className="spell-picker-filters">
          <label>
            {t("spells.class")}
            <select value={selectedClass} onChange={(event) => setSelectedClass(event.target.value)}>
              <option value="">{t("spells.allClasses")}</option>
              {classes.map((spellClass) => (
                <option key={spellClass} value={spellClass}>{spellClass}</option>
              ))}
            </select>
          </label>
          <label>
            {t("spells.circle")}
            <select value={selectedLevel} onChange={(event) => setSelectedLevel(event.target.value)}>
              <option value="">{t("spells.allCircles")}</option>
              <option value="0">{t("spells.cantrip")} (0)</option>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((level) => (
                <option key={level} value={level}>{t("spells.circle")} {level}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="spell-picker-count">{t("spells.found", { count: spells.length })}</div>
        <div className="spell-picker-list">
          {spells.map((spell) => (
            <button
              key={spell.id}
              type="button"
              className="spell-picker-item"
              disabled={selectedCatalogIds.has(spell.id)}
              onClick={() => onSelect(spell)}
            >
              <strong>{useEnglishCatalog ? spell.name.en : spell.name.ru}</strong>
              <small>{useEnglishCatalog ? spell.name.ru : spell.name.en}</small>
              <small>{formatClasses({
                ...spell,
                classLevels: Object.fromEntries(
                  Object.entries(spell.classLevels).filter(([spellClass]) =>
                    allowedSpellLists.length === 0 || allowedSpellLists.includes(spellClass)
                  )
                ),
              })}</small>
            </button>
          ))}
        </div>
      </DraggableModal>
    )
  );
};

export default SpellPickerModal;
export { resolveAllowedSpellLists };
